import React, { useContext, useState } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ApiContext } from '@api/api';
import { TargetEnum } from '@api/ringil-api.v2.tsx';
import { PushContext, PushMessage } from '@hooks/usePush/PushContext.tsx';
import { usePushEvent } from '@hooks/usePush/usePushEvent.ts';
import ResponsiveLayout from '@app/layout/ResponsiveLayout.tsx';
import { Button } from 'primereact/button';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { InputText } from 'primereact/inputtext';

/*
 * Hidden E2E verification page for the Centrifugo push infrastructure (direct URL: /en/test/pushpoc).
 * Dev-only, no i18n, no polish.
 */

interface LogEntry {
  at: string;
  message: PushMessage;
}

const PushPocPage: React.FC = () => {
  const { testPushPublish } = useContext(ApiContext);
  const { connectionState, reconnectCount } = useContext(PushContext);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [target, setTarget] = useState<TargetEnum>(TargetEnum.PERSONAL);
  const [message, setMessage] = useState('hello');

  usePushEvent(
    m => setLog(prev => [{ at: new Date().toISOString(), message: m }, ...prev].slice(0, 50)),
    {
      // demo events + real job hints, so a running multiduplication is visible here too
      types: ['demo.test', 'job.progress', 'job.finished'],
      onReconnect: () => setLog(prev => [{ at: new Date().toISOString(), message: { type: '_reconnected' } }, ...prev]),
    },
  );

  return (
    <ResponsiveLayout title={'Push PoC'}>
      <div className={'flex flex-col gap-4 py-4'}>
        <div className={'flex flex-row gap-4 items-center'}>
          <span>
            Connection: <b>{connectionState}</b>
          </span>
          <span>
            Reconnects: <b>{reconnectCount}</b>
          </span>
        </div>

        <div className={'flex flex-row gap-2 items-center'}>
          <Dropdown
            value={target}
            options={[TargetEnum.BROADCAST, TargetEnum.PERSONAL, TargetEnum.COMPANY]}
            onChange={e => setTarget(e.value)}
          />
          <InputText value={message} onChange={e => setMessage(e.target.value)} />
          <Button
            label={'Publish (admin)'}
            onClick={() => testPushPublish({ target, message }, d => console.log('published to', d.channel))}
          />
        </div>

        <div className={'flex flex-col gap-1 font-mono text-sm'}>
          {log.map((entry, i) => (
            <div key={i}>
              {entry.at} — {JSON.stringify(entry.message)}
            </div>
          ))}
        </div>
      </div>
    </ResponsiveLayout>
  );
};

export default withAuthenticationRequired(PushPocPage);
